import React, { useCallback } from "react";
import { MediaQuery, useResponsive } from "@outplayed/responsive";
import { ReactComponent as FilterIcon } from "svg/filters-icon.svg";
import RoleFilter from "../../FilterManager/Filters/RoleFilter";
import EncounterType from "../../FilterManager/Filters/EncounterType";
import DifficultyFilter from "../../FilterManager/Filters/DifficultyFilter";
import { useHistory, useLocation } from "react-router";
import { useQueryString } from "hooks/general-hooks";
import { validParams } from "query_parameters/valid_parameters";
import { updateQueryParamsIfChanged, buildQueryParams } from "../../../lib/general-helper";
import Encounter from "../../FilterManager/Filters/Encounter";
import StatToggle from "../../FilterManager/Filters/StatToggle";
import Affix from "../../FilterManager/Filters/Affix";
import PvPGameModes from "../../FilterManager/Filters/PvPGameModes.js";
import NHSFilter from "components/FilterManager/Filters/NHSFilter";

const TierListFilters = ({ role, defaultParams }) => {
  const queryParams = useQueryString();
  const location = useLocation();
  const history = useHistory();

  const pathArr = location.pathname.split("/");
  const tierListInfo = pathArr[3]?.toLowerCase() ?? "";
  let encounterType = pathArr[4]?.toLowerCase() ?? "";

  const onFilterChange = useCallback(
    (filterName, filterValue) => {
      let newFilter = { ...queryParams, [filterName]: filterValue };
      // If changing the encounter type, then need to update the encounter as well
      if (filterName === "encounterType") {
        const value = filterValue === "mythic+" ? "all_dungeons" : "all_bosses";
        newFilter = { ...newFilter, ["encounter"]: value };
        if (filterValue === "mythic+") {
          delete newFilter["difficulty"];
        }
      }
      // If changing the role type to dps, need to get rid of stat type
      if (filterName === "role" && filterValue === "dps") {
        delete newFilter["stat"];
      }
      updateQueryParamsIfChanged(history, location, newFilter);
    },
    [queryParams, location, history],
  );

  const onTierListFilterChange = useCallback(
    (filterName, filterValue) => {
      let newFilter = { ...queryParams, [filterName]: filterValue };

      // If changing the encounter type, then need to update the encounter as well
      if (filterName === "encounterType") {
        if (filterValue === "mythic+" || filterValue === "") {
          delete newFilter["difficulty"];
          delete newFilter["encounter"];
          delete newFilter["gameMode"];
          delete newFilter["hscombined"];
        }
        if (filterValue === "pvp" || filterValue === "") {
          delete newFilter["difficulty"];
          delete newFilter["encounter"];
          delete newFilter["affix"];
          delete newFilter["stat"];
          delete newFilter["hscombined"];
        }
        if (filterValue === "raid") {
          delete newFilter["affix"];
          delete newFilter["gameMode"];
          newFilter = { ...newFilter, ["encounter"]: "all_bosses" };
        }
      }
      // If changing the role type to dps, need to get rid of stat type
      if (filterName === "role" && filterValue === "dps") {
        delete newFilter["stat"];
      }

      let newTierListInfo = tierListInfo;
      if (filterName === "role") {
        if (filterValue === "heal") {
          newTierListInfo = "healer-rankings";
        } else if (filterValue === "tank") {
          newTierListInfo = "tank-rankings";
        } else {
          newTierListInfo = "dps-rankings";
        }
      }

      if (filterName === "encounterType") {
        encounterType = filterValue;
        delete newFilter["encounterType"];
      } else if (filterName === "role") {
        delete newFilter["role"];
      }

      if (encounterType === "mythic+" || encounterType === "") {
        encounterType = "";
      } else {
        encounterType = "/" + encounterType;
      }

      let newpath = {
        pathname: "/wow/tier-list/" + newTierListInfo + encounterType,
        search: `?${buildQueryParams(newFilter)}`,
      };

      if (filterName === "encounterType" || filterName === "role") {
        history.push(newpath);
      } else {
        updateQueryParamsIfChanged(history, newpath, newFilter);
      }
    },
    [queryParams, location, history],
  );

  const { currentBreakpoint } = useResponsive();
  const isMobile =
    currentBreakpoint === "MOBILE_SMALL" || currentBreakpoint === "MOBILE_MEDIUM" || currentBreakpoint === "MOBILE_LARGE";
  let validPageParams = validParams["tierList"] || {};
  const options = { ...validPageParams };

  const filters = {
    ...defaultParams,
    ...queryParams,
  };

  const selectedDungeon = filters["dungeon"]
    ? filters["dungeon"]
    : encounterType === "raid"
    ? "midnight_season_one_raid"
    : "all_dungeons";
  const selectedEncounter = filters["encounter"]
    ? filters["encounter"]
    : encounterType == "mythic+"
    ? "all_dungeons"
    : "all_bosses";
  const encounterOptions = encounterType == "mythic+" ? options.mythics : options.encounters[selectedDungeon];

  return (
    <div className="filter-manager">
      <div className="filter-width-wrapper">
        <div className="filter-manager_container">
          <div className="filter-manager_label flex-items" style={{ gap: "8px" }}>
            <div className="filter-icon">
              <FilterIcon />
              <MediaQuery min="TABLET" max="DESKTOP_LARGE">
                <span className="label">Filters</span>
              </MediaQuery>
            </div>
            <RoleFilter selected={role} onChange={(val) => onTierListFilterChange("role", val)} />
            {role.toLowerCase() !== "dps" && encounterType !== "pvp" && (
              <StatToggle
                isMobile={isMobile}
                onClick={(value) => onFilterChange("stat", value === "hps" ? "dps" : "hps")}
                value={filters.stat ? filters.stat : role === "heal" ? "hps" : "dps"}
              />
            )}
            <EncounterType
              options={options.encounterTypes}
              selectedVal={encounterType}
              onFilterChange={(val) => onTierListFilterChange("encounterType", val)}
            />
            {encounterType === "raid" && (
              <DifficultyFilter
                options={options.difficulty}
                value={filters.difficulty}
                onFilterChange={onFilterChange}
                isMobile={isMobile}
              />
            )}
            <Encounter
              disabled={encounterType === "pvp"}
              options={encounterOptions}
              selectedVal={selectedEncounter}
              onFilterChange={onFilterChange}
              isMobile={isMobile}
            />
            {encounterType === "raid" && (
              <NHSFilter
                options={options.hscombined}
                value={filters.hscombined}
                onFilterChange={onFilterChange}
                isMobile={isMobile}
              />
            )}

            <Affix
              disabled={encounterType === "raid" || encounterType === "pvp"}
              isMobile={isMobile}
              options={options.affixes}
              selectedVal={filters["affix"] ?? "all"}
              onFilterChange={onFilterChange}
            />
            <PvPGameModes
              disabled={encounterType !== "pvp"}
              isMobile={isMobile}
              options={options.gameModes.filter((obj) => obj.value !== "solo_shuffle")}
              selectedVal={filters["gameMode"] ?? "3v3"}
              onFilterChange={onFilterChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TierListFilters;
